import type { KeyboardEvent } from 'react';
import { FloatingFieldLabel } from '@/components/common/FloatingFieldLabel';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { FLOATING_FIELD_BOX_CLASS, FLOATING_INPUT_PLACEHOLDER_CLASS } from '@/styles/floatingField';
import { FilterDateField } from './FilterDateField';
import { FilterMultiSelectField } from './FilterMultiSelectField';
import { FilterSelectField } from './FilterSelectField';
import { FILTER_CONTROL_CLASS } from './advancedFilter.constants';
import type { FilterField } from './advancedFilter.types';
import { getDateLimits } from './dateLimits';

const DISABLED_FIELD_HINT_CLASS =
  'cursor-not-allowed rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring/50 [&_button:disabled]:pointer-events-none';
const SUBMIT_KEY = 'Enter';

interface FilterFieldItemProps {
  field: FilterField;
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  onSubmit?: (() => void) | undefined;
}

export function FilterFieldItem({ field, values, onChange, onSubmit }: FilterFieldItemProps) {
  const value = values[field.key] ?? '';

  function handleChange(nextValue: string) {
    onChange({ ...values, [field.key]: nextValue });
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === SUBMIT_KEY) onSubmit?.();
  }

  function renderControl() {
    if (field.type === 'select') {
      return <FilterSelectField field={field} value={value} onChange={handleChange} />;
    }
    if (field.type === 'multiSelect') {
      return <FilterMultiSelectField field={field} value={value} onChange={handleChange} />;
    }
    if (field.type === 'date') {
      const { minDate, maxDate } = getDateLimits(field, values);
      return (
        <FilterDateField
          field={field}
          value={value}
          onChange={handleChange}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
    }
    return (
      <Input
        type={field.type}
        value={value}
        onChange={(event) => handleChange(event.target.value)}
        onKeyDown={handleInputKeyDown}
        placeholder={field.placeholder ?? field.label}
        className={cn(FILTER_CONTROL_CLASS, FLOATING_INPUT_PLACEHOLDER_CLASS)}
      />
    );
  }

  const fieldContent = (
    <>
      {renderControl()}
      <FloatingFieldLabel isRequired={field.isRequired}>{field.label}</FloatingFieldLabel>
    </>
  );
  const filledState = value ? '' : undefined;

  if (!field.disabled || !field.disabledHint) {
    return (
      <div data-filled={filledState} className={FLOATING_FIELD_BOX_CLASS}>
        {fieldContent}
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={<div data-filled={filledState} />}
        tabIndex={0}
        aria-label={`${field.label}: ${field.disabledHint}`}
        className={cn(FLOATING_FIELD_BOX_CLASS, DISABLED_FIELD_HINT_CLASS)}
      >
        {fieldContent}
      </TooltipTrigger>
      <TooltipContent>{field.disabledHint}</TooltipContent>
    </Tooltip>
  );
}
